import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Save, Send, MapPin, IndianRupee, Calendar, Clock, Layers, Lock, ShieldCheck, X, Paperclip } from "lucide-react";
import api from "../../lib/api";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { companyNav } from "../../components/shared/navConfig";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { FileUpload } from "../../components/shared/FileUpload";
import { CATEGORIES, SKILLS, CITIES } from "../../data/mockData";
import { toast } from "sonner";

export default function PostTask() {
  const [selSkills, setSelSkills] = useState(["GST", "Tally"]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [form, setForm] = useState({
    title: "",
    category: "Accounting & Tax",
    description: "",
    education: "",
    experience: "",
    work_mode: "On-site",
    working_hours: "",
    budget: 8500,
    city: "",
    address: "",
    start_date: "",
    start_time: "",
    deadline: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/profile/me"); setProfile(data.profile); }
      catch {} finally { setLoading(false); }
    })();
  }, []);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleSkill = (s) => setSelSkills(x => x.includes(s) ? x.filter(k => k !== s) : [...x, s]);
  const verified = profile?.verification_status === "approved";

  const submit = async (asDraft = false) => {
    if (!verified && !asDraft) { toast.error("Company verification required"); return; }
    if (!form.title || !form.description) { toast.error("Title and description are required"); return; }
    setBusy(true);
    try {
      await api.post("/tasks/post", {
        ...form, skills: selSkills, budget: Number(form.budget) || 0,
        attachments: attachments.map(a => a.file_id),
        save_as_draft: asDraft,
      });
      toast.success(asDraft ? "Draft saved" : "Task submitted for admin approval");
      setTimeout(() => navigate("/company/tasks"), 900);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <DashboardShell nav={companyNav} title="Post a task"><div className="text-sm text-zinc-500">Loading…</div></DashboardShell>;
  }

  if (!verified) {
    const st = profile?.verification_status || "not_started";
    return (
      <DashboardShell nav={companyNav} title="Post a task" subtitle="Company verification required">
        <div className="card-surface p-10 max-w-xl relative overflow-hidden">
          <div className="absolute inset-0 grid-bg-fine opacity-60"/>
          <div className="relative">
            <div className="w-12 h-12 rounded-full mb-5 flex items-center justify-center"
                 style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.35)" }}>
              <Lock size={20} className="text-amber-400"/>
            </div>
            <h3 className="font-display text-2xl text-white">
              {st === "rejected" ? "Your verification was rejected" :
               st === "pending" ? "Verification is under review" : "Verify your company first"}
            </h3>
            <p className="text-sm text-zinc-500 mt-2">
              {st === "rejected" ? "Please update your KYC / GST details and resubmit." :
               st === "pending" ? "Our team is reviewing your KYC and GST documents. You'll be notified within 4 hours." :
                                   "Complete company onboarding — KYC + GST + address — before publishing tasks."}
            </p>
            <div className="mt-4 flex items-center gap-3">
              <StatusBadge status={st === "approved" ? "Approved" : st === "rejected" ? "Rejected" : "Pending"}/>
              <Link to="/company/onboarding" data-testid="post-onboarding-link" className="btn-primary px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5">
                <ShieldCheck size={12}/> {st === "not_started" ? "Start verification" : "Update verification"}
              </Link>
            </div>
          </div>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell nav={companyNav} title="Post a task" subtitle="Provide accurate details to attract the right talent">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Task basics" eyebrow="01">
            <Row2>
              <Field label="Task title"><input data-testid="post-title-input" value={form.title} onChange={e => setF("title", e.target.value)} placeholder="Quarterly GST Return Filing Support" className={ic}/></Field>
              <Field label="Category">
                <select data-testid="post-category-select" value={form.category} onChange={e => setF("category", e.target.value)} className={ic + " appearance-none"}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </Field>
            </Row2>
            <Field label="Description">
              <textarea data-testid="post-description-textarea" rows={4} value={form.description} onChange={e => setF("description", e.target.value)} placeholder="Describe scope, deliverables and acceptance criteria" className={ic + " resize-none"}/>
            </Field>
          </Section>

          <Section title="Requirements" eyebrow="02">
            <Row2>
              <Field label="Education"><input data-testid="post-education-input" value={form.education} onChange={e => setF("education", e.target.value)} placeholder="B.Com / M.Com" className={ic}/></Field>
              <Field label="Experience"><input data-testid="post-experience-input" value={form.experience} onChange={e => setF("experience", e.target.value)} placeholder="0-2 years" className={ic}/></Field>
            </Row2>
            <Field label="Required skills">
              <div className="flex flex-wrap gap-1.5">
                {SKILLS.map(s => {
                  const on = selSkills.includes(s);
                  return <button key={s} type="button" data-testid={`post-skill-${s}`} onClick={() => toggleSkill(s)}
                                 className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors duration-200 ${on ? "border-blue-500/40 bg-blue-500/10 text-blue-300" : "border-[#27272A] text-zinc-400 hover:border-[#3A3A42]"}`}>
                    {s}
                  </button>;
                })}
              </div>
            </Field>
          </Section>

          <Section title="Schedule & compensation" eyebrow="03">
            <Row2>
              <Field label="Work mode">
                <select data-testid="post-mode-select" value={form.work_mode} onChange={e => setF("work_mode", e.target.value)} className={ic + " appearance-none"}>
                  <option>On-site</option><option>Hybrid</option><option>Remote</option>
                </select>
              </Field>
              <Field label="Working hours"><input data-testid="post-hours-input" value={form.working_hours} onChange={e => setF("working_hours", e.target.value)} placeholder="6 hrs/day, 5 days" className={ic}/></Field>
              <Field label="Start date"><input type="date" data-testid="post-start-date-input" value={form.start_date} onChange={e => setF("start_date", e.target.value)} className={ic}/></Field>
              <Field label="Start time"><input type="time" data-testid="post-start-time-input" value={form.start_time} onChange={e => setF("start_time", e.target.value)} className={ic}/></Field>
              <Field label="Deadline"><input type="date" data-testid="post-deadline-input" value={form.deadline} onChange={e => setF("deadline", e.target.value)} className={ic}/></Field>
              <Field label="Budget (₹)">
                <div className={ic + " flex items-center gap-1"}>
                  <IndianRupee size={13} className="text-zinc-500"/>
                  <input data-testid="post-budget-input" type="number" value={form.budget} onChange={e => setF("budget", e.target.value)} className="bg-transparent outline-none text-sm text-white flex-1"/>
                </div>
              </Field>
            </Row2>
          </Section>

          <Section title="Location & attachments" eyebrow="04">
            <Row2>
              <Field label="City">
                <select data-testid="post-city-select" value={form.city} onChange={e => setF("city", e.target.value)} className={ic + " appearance-none"}>
                  <option value="">Select…</option>{CITIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Address"><input data-testid="post-address-input" value={form.address} onChange={e => setF("address", e.target.value)} placeholder="Full task location" className={ic}/></Field>
            </Row2>

            <div className="space-y-3">
              {attachments.map((a, i) => (
                <div key={a.file_id} className="flex items-center gap-3 p-3 rounded-md border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                  <Paperclip size={13} className="text-blue-400"/>
                  <span className="text-sm text-white flex-1 truncate">{a.filename}</span>
                  <button data-testid={`post-attach-remove-${i}`} onClick={() => setAttachments(a2 => a2.filter((_, k) => k !== i))} className="text-zinc-500 hover:text-rose-400"><X size={13}/></button>
                </div>
              ))}
              <FileUpload
                value={null}
                onChange={v => v && setAttachments(a => [...a, v])}
                purpose="task_attachment"
                testId="post-attach-upload"
                label="Add attachments (reference docs, PDFs, spreadsheets)"
                hint="Choose file to add"
              />
            </div>
          </Section>
        </div>

        {/* Preview */}
        <aside className="card-surface p-5 h-fit sticky top-24">
          <div className="eyebrow">Preview</div>
          <h3 className="font-display text-lg text-white mt-1">Task summary</h3>
          <div className="divider-h my-4"/>
          <PreviewRow icon={Layers}     l="Category"    v={form.category || "—"}/>
          <PreviewRow icon={MapPin}     l="Location"    v={`${form.city || "—"} · ${form.work_mode}`}/>
          <PreviewRow icon={Calendar}   l="Deadline"    v={form.deadline || "—"}/>
          <PreviewRow icon={IndianRupee}l="Budget"      v={`₹${Number(form.budget || 0).toLocaleString()}`}/>
          <PreviewRow icon={Clock}      l="Hours"       v={form.working_hours || "—"}/>
          <PreviewRow icon={Paperclip}  l="Attachments" v={attachments.length}/>

          <div className="flex flex-col gap-2 mt-5">
            <button data-testid="post-submit-btn" onClick={() => submit(false)} disabled={busy} className="btn-primary py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
              <Send size={13}/> Submit for approval
            </button>
            <button data-testid="post-draft-btn" onClick={() => submit(true)} disabled={busy} className="btn-ghost py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
              <Save size={13}/> Save draft
            </button>
          </div>
          <div className="text-[11px] text-zinc-500 mt-3 text-center">Tasks go live after admin review (typically &lt; 4 hrs)</div>
        </aside>
      </div>
    </DashboardShell>
  );
}

const ic = "w-full px-3 py-2.5 rounded-md border bg-[#111114] text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200 focus:border-blue-500/50 border-[#27272A]";
const Section = ({ title, eyebrow, children }) => (
  <div className="card-surface p-6">
    <div className="flex items-baseline gap-3 mb-5">
      <span className="font-mono text-xs text-blue-400">{eyebrow}</span>
      <h3 className="font-display text-lg text-white">{title}</h3>
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);
const Row2 = ({ children }) => <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
const PreviewRow = ({ icon: Icon, l, v }) => (
  <div className="flex items-center gap-3 py-2 border-b last:border-b-0" style={{ borderColor: "var(--border)" }}>
    <Icon size={13} className="text-zinc-500"/>
    <span className="text-xs text-zinc-500 w-20">{l}</span>
    <span className="text-xs text-zinc-200 ml-auto truncate max-w-[160px]">{v}</span>
  </div>
);
